import {
  type ActionExample,
  type Content,
  type HandlerCallback,
  type AgentRuntime,
  type Memory,
  type State,
  type Action,
  logger,
  ModelType,
} from "@elizaos/core";
import axios from "axios";
import { z } from "zod";
import { getApiConfig, validateCoingeckoConfig } from "../environment";
import { getTrendingPoolsTemplate } from "../templates/trendingPools";

interface TrendingPool {
  id: string;
  type: string;
  attributes: {
    name: string;
    market_cap_usd: string;
    fdv_usd: string;
    reserve_in_usd: string;
    pool_created_at: string;
  };
}

interface TrendingPoolsResponse {
  data: TrendingPool[];
}

export const GetTrendingPoolsSchema = z.object({
  limit: z.number().min(1).max(100).default(10),
});

export type GetTrendingPoolsContent = z.infer<typeof GetTrendingPoolsSchema> &
  Content;

export const isGetTrendingPoolsContent = (
  obj: unknown
): obj is GetTrendingPoolsContent => {
  return GetTrendingPoolsSchema.safeParse(obj).success;
};

export default {
  name: "GET_TRENDING_POOLS",
  similes: ["TRENDING_POOLS", "HOT_POOLS", "POPULAR_POOLS", "TOP_POOLS"],
  validate: async (runtime: AgentRuntime, _message: Memory) => {
    await validateCoingeckoConfig(runtime);
    return true;
  },
  description: "Get list of trending pools from CoinGecko's onchain data",
  handler: async (
    runtime: AgentRuntime,
    message: Memory,
    state: State,
    _options: { [key: string]: unknown },
    callback?: HandlerCallback
  ): Promise<boolean> => {
    logger.log("Starting CoinGecko GET_TRENDING_POOLS handler...");

    // Initialize or update state
    let currentState = state;
    if (!currentState) {
      currentState = (await runtime.composeState(message)) as State;
    } else {
      currentState = await runtime.composeState(message);
    }

    try {
      logger.log("Composing trending pools context...");
      const trendingContext = getTrendingPoolsTemplate;

      const result = await runtime.useModel(ModelType.TEXT_LARGE, {
        prompt: trendingContext,

        schema: GetTrendingPoolsSchema,
      });

      if (!isGetTrendingPoolsContent(result)) {
        logger.error("Invalid trending pools request format");
        return false;
      }

      // Fetch trending pools data from CoinGecko
      const config = await validateCoingeckoConfig(runtime);
      const { baseUrl, apiKey, headerKey } = getApiConfig(config);

      logger.log("Fetching trending pools data...");

      const response = await axios.get<TrendingPoolsResponse>(
        `${baseUrl}/onchain/networks/trending_pools?include=base_token,dex`,
        {
          headers: {
            [headerKey]: apiKey,
          },
        }
      );

      if (!response.data) {
        throw new Error("No data received from CoinGecko API");
      }

      const formattedData = response.data.data.map((pool) => ({
        name: pool.attributes.name,
        marketCap: Number(pool.attributes.market_cap_usd).toLocaleString(
          "en-US",
          {
            style: "currency",
            currency: "USD",
          }
        ),
        fdv: Number(pool.attributes.fdv_usd).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        reserveUSD: Number(pool.attributes.reserve_in_usd).toLocaleString(
          "en-US",
          {
            style: "currency",
            currency: "USD",
          }
        ),
        createdAt: new Date(
          pool.attributes.pool_created_at
        ).toLocaleDateString(),
      }));

      const responseText = [
        "Trending Pools Overview:",
        "",
        ...formattedData.map((pool, index) =>
          [
            `${index + 1}. ${pool.name}`,
            `   Market Cap: ${pool.marketCap}`,
            `   FDV: ${pool.fdv}`,
            `   Reserve: ${pool.reserveUSD}`,
            `   Created: ${pool.createdAt}`,
            "",
          ].join("\n")
        ),
      ].join("\n");

      logger.success("Trending pools data retrieved successfully!");

      if (callback) {
        callback({
          text: responseText,
          content: {
            trendingPools: formattedData,
            timestamp: new Date().toISOString(),
          },
        });
      }

      return true;
    } catch (error) {
      logger.error("Error in GET_TRENDING_POOLS handler:", error);

      const errorMessage =
        error.response?.status === 429
          ? "Rate limit exceeded. Please try again later."
          : `Error fetching trending pools data: ${error.message}`;

      if (callback) {
        callback({
          text: errorMessage,
          content: {
            error: error.message,
            statusCode: error.response?.status,
          },
        });
      }
      return false;
    }
  },

  examples: [
    [
      {
        name: "{{user1}}",
        content: {
          text: "Show me trending liquidity pools",
        },
      },
      {
        name: "{{agent}}",
        content: {
          text: "I'll check the trending liquidity pools for you.",
          action: "GET_TRENDING_POOLS",
        },
      },
      {
        name: "{{agent}}",
        content: {
          text: "Here are the trending liquidity pools:\n1. MELANIA / USDC\n   Market Cap: $954,636,707\n   FDV: $6,402,478,508\n   Reserve: $363,641,037\n   Created: 1/19/2025\n2. TRUMP / USDC\n   Market Cap: $8,844,297,825\n   FDV: $43,874,068,484\n   Reserve: $718,413,745\n   Created: 1/17/2025",
        },
      },
    ],
    [
      {
        name: "{{user1}}",
        content: {
          text: "What are the top hottest dex pools?",
        },
      },
      {
        name: "{{agent}}",
        content: {
          text: "I'll fetch the top hottest DEX pools for you.",
          action: "GET_TRENDING_POOLS",
        },
      },
      {
        name: "{{agent}}",
        content: {
          text: "Here are the top 5 hottest DEX pools:\n1. TRUMP / USDC\n   Market Cap: $8,844,297,825\n   FDV: $43,874,068,484\n   Reserve: $718,413,745\n   Created: 1/17/2025\n2. MELANIA / USDC\n   Market Cap: $954,636,707\n   FDV: $6,402,478,508\n   Reserve: $363,641,037\n   Created: 1/19/2025",
        },
      },
    ],
    [
      {
        name: "{{user1}}",
        content: {
          text: "List all trading pools with highest volume",
        },
      },
      {
        name: "{{agent}}",
        content: {
          text: "I'll get all the trending trading pools for you.",
          action: "GET_TRENDING_POOLS",
        },
      },
      {
        name: "{{agent}}",
        content: {
          text: "Here are all trending trading pools:\n1. MELANIA / USDC\n   Market Cap: $954,636,707\n   FDV: $6,402,478,508\n   Reserve: $363,641,037\n   Created: 1/19/2025\n2. TRUMP / USDC\n   Market Cap: $8,844,297,825\n   FDV: $43,874,068,484\n   Reserve: $718,413,745\n   Created: 1/17/2025",
        },
      },
    ],
  ] as ActionExample[][],
} as Action;
